import './TrainingPropertiesList.css'

import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DetailList, Heading } from '@orobix/orobix-fe-components'
import { format } from 'date-fns'
import { enUS } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useStore } from '../../store/store'
import {
  Training,
  TrainingMetadataAnomaly,
  TrainingMetadataPatch,
  TrainingMetadataPrecision,
  TrainingMetadataResolution,
} from '../../store/store.types'
import { fetchDatasetsUsedInTraining } from '../../store/storeActions'
import { formatDate } from '../../utils/date'
import { getConfusionMatrixMetric } from '../../utils/metric'
import { getProductTypeName } from '../../utils/products'
import {
  getOverallPerformanceName,
  getTrainingStatusNames,
  toScore,
} from '../../utils/trainings'
import ConfusionMatrix from '../ConfusionMatrix/ConfusionMatrix'
import HtmlModal from '../HtmlModal/HtmlModal'
import { AnomalyStrategyProperty } from './AnomalyStrategyProperty'
import useStyles from './TrainingPropertiesList.styles'
import { getConfidence } from './utils'

interface TrainingPropertiesListProps {
  type: 'complete' | 'summary'
}

const TrainingPropertiesList = ({ type }: TrainingPropertiesListProps) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const { training, product } = useStore()

  const isComplete = type === 'complete'

  const hasAccuracyDetails =
    training &&
    training.metrics.filter((m) => m.schemaType === 'confusion_matrix').length >
      0

  const trainingMetadataPatch =
    training &&
    training.product.type === 'classification_patch' &&
    (training.metadata as TrainingMetadataPatch)

  const trainingMetadataAnomaly =
    training &&
    training.product.type === 'anomaly' &&
    (training.metadata as TrainingMetadataAnomaly)

  const confusionMatrix =
    training && getConfusionMatrixMetric(training.metrics)?.data

  const confidence = getConfidence(training)

  const isCustomResolutionUsed = (train: Training) =>
    ['anomaly', 'segmentation'].includes(train.product.type) &&
    train.metadata &&
    (train?.metadata as TrainingMetadataResolution).inputWidth &&
    (train?.metadata as TrainingMetadataResolution).inputHeight

  const getTrainingResolution = (train: Training) => {
    const width = `${
      (train?.metadata as TrainingMetadataResolution).inputWidth
    }`
    const height = `${
      (train?.metadata as TrainingMetadataResolution).inputHeight
    }`
    return `${width}x${height} px`
  }
  const [htmlHelperModal, setHtmlHelperModal] = useState(false)
  const [datasetsUsedInTraining, setDatasetsUsedInTraining] = useState<
    Array<{ id: number; name: string; updateDate: string }>
  >([])

  const precision = (training?.metadata as TrainingMetadataPrecision)?.precision

  useEffect(() => {
    if (training?.id && isComplete) {
      fetchDatasetsUsedInTraining(training.id)
        .then((datasets) => {
          setDatasetsUsedInTraining(datasets)
        })
        .catch(() => {
          // eslint-disable-next-line no-console
          console.error('Error fetching datasets used in training')
        })
    }
  }, [training?.id, isComplete])

  return training && product ? (
    <>
      <DetailList
        titleColumnWidth="12rem"
        list={[
          {
            title: t('Status'),
            value: getTrainingStatusNames(training.status),
          },
          ...(confidence && isComplete
            ? [
                {
                  title: t('Confidence'),
                  value: <>{toScore(confidence)}</>,
                },
              ]
            : []),

          {
            title: t('Created on'),
            value: formatDate(training.creationDate),
          },
          ...(training.overallPerformance && !isComplete
            ? [
                {
                  title: getOverallPerformanceName(
                    training.overallPerformance.schemaType
                  ),
                  value: (
                    <>
                      {toScore(training.overallPerformance.data.value)}
                      {training && (
                        <FontAwesomeIcon
                          icon={faCircleQuestion}
                          onClick={() => setHtmlHelperModal(true)}
                        />
                      )}
                    </>
                  ),
                },
              ]
            : []),

          ...(training.notes && isComplete
            ? [
                {
                  title: t('Notes'),
                  value: training.notes,
                },
              ]
            : []),
          ...(isCustomResolutionUsed(training)
            ? [
                {
                  title: t('Resolution'),
                  value: getTrainingResolution(training),
                },
              ]
            : []),
        ]}
      />

      {isComplete && (
        <>
          <Heading headingLevel="3" headingLevelStyle="4" noBottomMargin>
            {t('Training params')}
          </Heading>

          <div
            className={
              datasetsUsedInTraining.length > 0
                ? 'training-properties-datasets-row'
                : undefined
            }
          >
            <DetailList
              titleColumnWidth="12rem"
              list={[
                {
                  title: t('Training type'),
                  value: getProductTypeName(product.type),
                },

                ...(trainingMetadataPatch
                  ? [
                      {
                        title: t('Horizontal splitting'),
                        value: trainingMetadataPatch.horizontalPatches,
                      },
                      {
                        title: t('Vertical splitting'),
                        value: trainingMetadataPatch.verticalPatches,
                      },
                      {
                        title: t('Overlap'),
                        value: trainingMetadataPatch.patchesOverlap,
                      },
                      {
                        title: t('Classes priority'),
                        value: trainingMetadataPatch.patchesClassPriority.join(
                          ', '
                        ),
                      },
                    ]
                  : []),

                ...(trainingMetadataAnomaly
                  ? [
                      {
                        title: t('Optimal threshold'),
                        value: 100,
                      },
                      {
                        title: t('Anomaly strategy'),
                        value: (
                          <AnomalyStrategyProperty
                            anomalyStrategyId={
                              trainingMetadataAnomaly.anomalyStrategyId
                            }
                          />
                        ),
                      },
                    ]
                  : []),

                {
                  title: t('Backbone'),
                  value:
                    training.backbone.name +
                    (training.backbone.isDemo
                      ? ' [🚧 demo - not available for download]'
                      : ''),
                },
                ...(precision
                  ? [{ title: t('Precision'), value: precision.toUpperCase() }]
                  : []),
                ...(datasetsUsedInTraining.length > 0
                  ? [
                      {
                        title: t('Datasets' as any),
                        value: (
                          <table className={`datasets-table ${classes.table}`}>
                            <thead>
                              <tr>
                                <th className={classes.th}>{t('Name')}</th>
                                <th className={classes.th}>
                                  {t('Updated on')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {datasetsUsedInTraining.map((dataset) => (
                                <tr key={dataset.id}>
                                  <td className={classes.td}>{dataset.name}</td>
                                  <td className={classes.td}>
                                    {dataset.updateDate
                                      ? `${format(
                                          new Date(dataset.updateDate),
                                          'PP',
                                          { locale: enUS }
                                        )} ${format(
                                          new Date(dataset.updateDate),
                                          'p',
                                          { locale: enUS }
                                        )}`
                                      : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        </>
      )}

      {isComplete &&
        hasAccuracyDetails &&
        training.overallPerformance &&
        confusionMatrix && (
          <ConfusionMatrix
            product={product}
            withDetails
            globalResultTitle={getOverallPerformanceName(
              training.overallPerformance.schemaType
            )}
            globalResultValue={toScore(training.overallPerformance.data.value)}
            data={confusionMatrix}
            samplesTypeTitle={
              {
                anomaly: t('Images'),
                classification: t('Images'),
                classification_patch: t('Patches'),
                segmentation: t('Images'),
              }[product.type]
            }
            isFineTuning={training.backbone.code === 'fine_tuning'}
          />
        )}
      {htmlHelperModal && training && (
        <HtmlModal
          productType={training.product.type}
          onCloseRequest={() => setHtmlHelperModal(false)}
        />
      )}
    </>
  ) : null
}

export default TrainingPropertiesList
